<template>
    <div class="main-content">
        <!-- TOASTS -->
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">{{ alert.message }}</div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0 flex-nowrap text-nowrap align-items-center">
                    <li class="breadcrumb-item"><router-link class="nav-link d-inline" to="/">Início</router-link></li>
                    <li class="breadcrumb-item"><router-link class="nav-link d-inline" to="/list-landing-pages">Landing Pages</router-link></li>
                    <li class="breadcrumb-item active text-dark small" aria-current="page">
                        {{ isEditing ? 'Editar Página' : 'Nova Página' }}
                    </li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom flex-wrap gap-3">
                <div>
                    <h4 class="mb-0 fw-bold">Page Builder</h4>
                    <span class="text-muted fs-7">Construa páginas incríveis com blocos modulares</span>
                </div>

                <div class="d-flex gap-2 flex-wrap">
                    <button class="btn btn-outline-primary rounded-pill px-3 shadow-sm d-flex align-items-center"
                        @click="openFullPreviewModal">
                        <i class="bi bi-display me-2"></i> Ver no Navegador
                    </button>
                    <button class="btn btn-pmg-light rounded-pill px-3 shadow-sm d-flex align-items-center border-warning text-dark"
                        @click="openAiModal">
                        <i class="bi bi-magic me-2 text-warning"></i> Criar com IA
                    </button>
                    <button class="btn btn-pmg-primary" @click="savePage" :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <i v-else class="bi bi-floppy me-1"></i>
                        {{ loading ? 'Salvando...' : (isEditing ? 'Atualizar Página' : 'Salvar Página') }}
                    </button>
                </div>
            </div>

            <div class="row g-4">
                <!-- COLUNA ESQUERDA: EDITOR -->
                <div class="col-lg-7">

                    <!-- CONFIGURAÇÃO INICIAL -->
                    <div class="card bg-light border-0 rounded-4 p-3 mb-4">
                        <h6 class="fw-bold mb-3 text-secondary">Configuração Inicial</h6>
                        <div class="row g-3">
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted">Título da Página</label>
                                <input v-model="form.title" type="text" class="form-control form-control-pmg" placeholder="Título...">
                            </div>
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted">Template Inicial</label>
                                <select v-model="form.template" class="form-select form-control-pmg">
                                    <option value="">Página em Branco</option>
                                    <option value="layout_1">Layout Moderno (1)</option>
                                    <option value="layout_2">Layout Clean (2)</option>
                                    <option value="layout_6">Campanha de Arrecadação (RMR)</option>
                                </select>
                            </div>
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted mb-1">Cor do Título da Página</label>
                                <div class="input-group input-group-sm">
                                    <input v-model="form.title_color" type="color" class="form-control form-control-color" style="width: 45px;">
                                    <input v-model="form.title_color" type="text" class="form-control" placeholder="#5F0487">
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- LOOP DE SEÇÕES (CONSTRUTOR) -->
                    <div class="card bg-light border-0 rounded-4 p-3 mb-4">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="fw-bold m-0 text-secondary">Conteúdo da Página (Blocos)</h6>
                        </div>

                        <div v-for="(section, index) in form.sections" :key="index"
                            class="card bg-white border rounded-4 p-3 mb-4 shadow-sm position-relative">

                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <div class="d-flex align-items-center gap-2">
                                    <span class="badge rounded-pill px-3 py-2 text-uppercase" style="background-color: #5F0487; color: white;">
                                        <i class="bi bi-grid-3x3-gap-fill me-1"></i> {{ section.type }}
                                    </span>
                                </div>
                                <div class="btn-group shadow-sm">
                                    <button class="btn btn-sm btn-light border" @click="moveSection(index, -1)" :disabled="index === 0"><i class="bi bi-arrow-up"></i></button>
                                    <button class="btn btn-sm btn-light border" @click="moveSection(index, 1)" :disabled="index === form.sections.length - 1"><i class="bi bi-arrow-down"></i></button>
                                    <button class="btn btn-sm btn-outline-danger" @click="removeSection(index)"><i class="bi bi-trash"></i></button>
                                </div>
                            </div>

                            <!-- CONTROLES DE CORES GERAIS -->
                            <div v-if="section.type !== 'dailypag_crowdfunding'" class="row g-3 mb-3 bg-light p-2 rounded-3 align-items-end">
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted mb-1">Cor de Fundo</label>
                                    <div class="input-group input-group-sm">
                                        <input v-model="section.content.bg_color" type="color" class="form-control form-control-color" style="width: 45px;">
                                        <input v-model="section.content.bg_color" type="text" class="form-control">
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted mb-1">Cor de Destaque / Título</label>
                                    <div class="input-group input-group-sm">
                                        <input v-model="section.content.accent_color" type="color" class="form-control form-control-color" style="width: 45px;">
                                        <input v-model="section.content.accent_color" type="text" class="form-control">
                                    </div>
                                </div>
                            </div>

                            <!-- 1. CAMPANHA DE ARRECADAÇÃO RMR -->
                            <div v-if="section.type === 'dailypag_crowdfunding'" class="section-body">
                                <h6 class="fw-bold border-bottom pb-2 mb-3" style="color: #5F0487;">1. Informações Principais</h6>
                                <div class="row g-2 mb-3">
                                    <div class="col-md-12">
                                        <label class="form-label small fw-bold text-muted">Título da Campanha</label>
                                        <input v-model="section.content.title" type="text" class="form-control form-control-sm">
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label small fw-bold text-muted">Categoria</label>
                                        <input v-model="section.content.category" type="text" class="form-control form-control-sm">
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label small fw-bold text-muted">Localização</label>
                                        <input v-model="section.content.location" type="text" class="form-control form-control-sm">
                                    </div>
                                    <div class="col-md-12">
                                        <label class="form-label small fw-bold text-muted">Imagem de Capa (Banner)</label>
                                        <ImageCropper :aspectRatio="16/9" @cropped="blob => handleCroppedBlob(blob, section.content, 'cover_image')" />
                                    </div>
                                </div>

                                <h6 class="fw-bold border-bottom pb-2 mb-3 mt-4" style="color: #5F0487;">2. Metas e Estatísticas</h6>
                                <div class="row g-2 mb-3">
                                    <div class="col-md-6">
                                        <label class="form-label small fw-bold text-muted">Valor Arrecadado (R$)</label>
                                        <input :value="formatCurrency(section.content.raised_amount)" @input="handleCurrencyInput($event, section.content, 'raised_amount')" type="text" class="form-control form-control-sm fw-bold text-success" placeholder="0,00">
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label small fw-bold text-muted">Meta Total (R$)</label>
                                        <input :value="formatCurrency(section.content.goal_amount)" @input="handleCurrencyInput($event, section.content, 'goal_amount')" type="text" class="form-control form-control-sm" placeholder="0,00">
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label small fw-bold text-muted">Doadores (Apoios)</label>
                                        <input v-model.number="section.content.supporters_count" type="number" class="form-control form-control-sm">
                                    </div>
                                </div>

                                <h6 class="fw-bold border-bottom pb-2 mb-3 mt-4" style="color: #5F0487;">3. Sobre a Campanha</h6>
                                <div class="mb-3">
                                    <label class="form-label small fw-bold text-muted">Resumo Breve</label>
                                    <textarea v-model="section.content.short_description" class="form-control form-control-sm" rows="2"></textarea>
                                </div>
                                <div class="mb-3">
                                    <label class="form-label small fw-bold text-muted">Chave PIX Rápida (Opcional)</label>
                                    <input v-model="section.content.pix_key" type="text" class="form-control form-control-sm">
                                </div>
                                <div class="mb-3">
                                    <label class="form-label small fw-bold text-muted mb-1">História Completa (Aba Sobre)</label>
                                    <div class="editor-toolbar border border-bottom-0 rounded-top-3 bg-light p-1 d-flex gap-1 align-items-center shadow-sm">
                                        <button type="button" class="btn btn-sm btn-light border-0 px-2 py-1" @click="formatText('bold')"><i class="bi bi-type-bold"></i></button>
                                        <button type="button" class="btn btn-sm btn-light border-0 px-2 py-1" @click="formatText('italic')"><i class="bi bi-type-italic"></i></button>
                                    </div>
                                    <div contenteditable="true" class="form-control form-control-pmg rounded-top-0 bg-white" style="min-height: 120px;"
                                        @blur="e => section.content.about_text = e.target.innerHTML" v-html="section.content.about_text">
                                    </div>
                                </div>

                                <h6 class="fw-bold border-bottom pb-2 mb-3 mt-4" style="color: #5F0487;">4. Checkout & Gateway</h6>
                                <div class="p-3 bg-light border rounded-3 mb-3">
                                    <div class="row g-3">
                                        <div class="col-md-6">
                                            <label class="form-label small text-muted mb-1">Nome Organizador</label>
                                            <input v-model="section.content.creator_name" type="text" class="form-control form-control-sm">
                                        </div>
                                        <div class="col-md-6">
                                            <label class="form-label small text-muted mb-1">Valor Base (R$)</label>
                                            <input :value="formatCurrency(section.content.amount)" @input="handleCurrencyInput($event, section.content, 'amount')" type="text" class="form-control form-control-sm" placeholder="0,00">
                                        </div>

                                        <!-- PADRÃO DE GATEWAY IDÊNTICO AO TRANSACTION MODAL[cite: 3] -->
                                        <div class="col-12 mt-3 pt-3 border-top border-info-subtle">
                                            <label class="form-label small fw-bold text-muted">
                                                Conta/Gateway de Recebimento
                                            </label>

                                            <select v-if="getAvailableGatewaysForSection(section).length > 1"
                                                v-model="section.content.payment_gateway_id"
                                                class="form-select form-control-pmg bg-white">
                                                <option :value="null" disabled>Escolha por onde receber...</option>
                                                <option v-for="g in getAvailableGatewaysForSection(section)" :key="g.id" :value="g.id">
                                                    {{ g.name }} ({{ g.provider.toUpperCase() }})
                                                </option>
                                            </select>

                                            <div v-else-if="getAvailableGatewaysForSection(section).length === 1"
                                                class="p-2 bg-white border rounded-3 d-flex align-items-center">
                                                <i class="bi bi-shield-check text-success me-2"></i>
                                                <span class="fw-bold text-dark">
                                                    {{ getAvailableGatewaysForSection(section)[0].name }}
                                                    <small class="text-muted fw-normal">({{ getAvailableGatewaysForSection(section)[0].provider.toUpperCase() }})</small>
                                                </span>
                                            </div>

                                            <div v-else class="text-danger small mt-1">
                                                <i class="bi bi-exclamation-triangle"></i> Nenhum gateway compatível com os métodos selecionados.
                                            </div>
                                        </div>

                                        <div class="col-12 mt-2">
                                            <label class="form-label small fw-bold text-muted d-block">Métodos Permitidos:</label>
                                            <div class="d-flex gap-3 flex-wrap">
                                                <div class="form-check">
                                                    <input class="form-check-input" type="checkbox" v-model="section.content.allowed_methods.pix" :id="'pay_pix_'+index">
                                                    <label class="form-check-label small" :for="'pay_pix_'+index">PIX</label>
                                                </div>
                                                <div class="form-check">
                                                    <input class="form-check-input" type="checkbox" v-model="section.content.allowed_methods.boleto" :id="'pay_bol_'+index">
                                                    <label class="form-check-label small" :for="'pay_bol_'+index">Boleto</label>
                                                </div>
                                                <div class="form-check">
                                                    <input class="form-check-input" type="checkbox" v-model="section.content.allowed_methods.credit_card" :id="'pay_cc_'+index">
                                                    <label class="form-check-label small" :for="'pay_cc_'+index">Cartão</label>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-12">
                                            <div class="form-check form-switch mt-1">
                                                <input class="form-check-input" type="checkbox" v-model="section.content.allows_fractional" :id="'frac_'+index">
                                                <label class="form-check-label small fw-bold" :for="'frac_'+index">Permitir doador escolher o valor (fracionado)?</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- DEMAIS BLOCOS... -->
                            <div v-else-if="section.type === 'text'">
                                <div class="mb-2">
                                    <label class="form-label small fw-bold text-muted mb-0">Título do Bloco (Opcional)</label>
                                    <input v-model="section.content.title" type="text" class="form-control form-control-sm mb-2">
                                </div>
                                <div contenteditable="true" class="form-control form-control-pmg bg-white" style="min-height: 100px;"
                                    @blur="e => section.content.text = e.target.innerHTML" v-html="section.content.text">
                                </div>
                            </div>
                            <div v-else-if="section.type === 'youtube'">
                                <label class="form-label small fw-bold text-muted">Título do Vídeo (Opcional)</label>
                                <input v-model="section.content.title" type="text" class="form-control form-control-sm mb-2">
                                <label class="form-label small fw-bold text-muted">URL do Vídeo</label>
                                <input v-model="section.content.url" type="text" class="form-control form-control-pmg">
                            </div>
                            <div v-else-if="section.type === 'contact'">
                                <label class="form-label small fw-bold text-muted">Título da Seção de Contato</label>
                                <input v-model="section.content.title" type="text" class="form-control form-control-sm mb-2" placeholder="Fale Conosco">
                                <div class="row g-2">
                                    <div class="col-md-6">
                                        <label class="form-label small text-muted mb-1">WhatsApp</label>
                                        <input v-model="section.content.whatsapp" type="text" class="form-control form-control-sm" placeholder="(11) 90000-0000">
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label small text-muted mb-1">E-mail</label>
                                        <input v-model="section.content.email" type="text" class="form-control form-control-sm" placeholder="contato@dailypag.com.br">
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="section.type === 'payment'">
                                <input v-model="section.content.button_text" type="text" class="form-control form-control-pmg mb-2" placeholder="Texto do Botão">
                                <select v-model="section.content.payment_gateway_id" class="form-select form-control-pmg mb-2">
                                    <option v-for="gw in paymentOptions" :key="gw.id" :value="gw.id">{{ gw.name }} ({{ gw.provider.toUpperCase() }})</option>
                                </select>
                                <input :value="formatCurrency(section.content.amount)" @input="handleCurrencyInput($event, section.content, 'amount')" type="text" class="form-control form-control-pmg" placeholder="Valor Base">
                            </div>
                            <div v-else-if="section.type === 'carousel'">
                                <input v-model="section.content.title" type="text" class="form-control form-control-pmg mb-3" placeholder="Título da galeria">
                                <div v-for="(img, imgIdx) in section.content.images" :key="imgIdx" class="mb-3 p-2 border rounded">
                                    <label class="form-label small text-muted mb-1">Imagem #{{ imgIdx + 1 }}</label>
                                    <div class="d-flex gap-2">
                                        <input type="file" class="form-control form-control-sm" accept="image/*" @change="handleImageUpload($event, img, 'url')">
                                        <button class="btn btn-outline-danger btn-sm" @click="section.content.images.splice(imgIdx, 1)"><i class="bi bi-trash"></i></button>
                                    </div>
                                </div>
                                <button class="btn btn-sm btn-outline-secondary w-100" @click="section.content.images.push({ url: '' })">+ Adicionar Imagem</button>
                            </div>

                        </div>

                        <!-- ADICIONAR NOVA SEÇÃO -->
                        <div class="text-center mt-3 p-3 border border-dashed rounded-3 bg-white" style="border-style: dashed !important;">
                            <span class="d-block small text-muted mb-3">Adicionar novo bloco à página</span>
                            <div class="d-flex flex-wrap justify-content-center gap-2">
                                <button class="btn btn-sm btn-pmg-light" style="border-color: #5F0487; color: #5F0487;" @click="addSection('dailypag_crowdfunding')">
                                    <i class="bi bi-box2-heart-fill"></i> Campanha RMR (Arrecadação)
                                </button>
                                <div class="w-100 mb-1"></div>
                                <button class="btn btn-sm btn-pmg-light" @click="addSection('text')"><i class="bi bi-text-paragraph"></i> Texto</button>
                                <button class="btn btn-sm btn-pmg-light" @click="addSection('youtube')"><i class="bi bi-youtube text-danger"></i> Vídeo</button>
                                <button class="btn btn-sm btn-pmg-light" @click="addSection('contact')"><i class="bi bi-telephone"></i> Contato</button>
                                <button class="btn btn-sm btn-pmg-light" @click="addSection('payment')"><i class="bi bi-credit-card text-success"></i> Pagamento</button>
                                <button class="btn btn-sm btn-pmg-light" @click="addSection('carousel')"><i class="bi bi-images text-info"></i> Carrossel</button>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- COLUNA DIREITA: LIVE PREVIEW INTERNO -->
                <div class="col-lg-5">
                    <div class="preview-container shadow-lg rounded-4 p-0 sticky-top bg-white d-flex flex-column overflow-hidden modern-preview-font"
                        style="top: 20px; height: 85vh; border: 8px solid #f1f5f9; background: #f8f9fa;">
                        
                        <PagePreviewRenderer :form="form" @openCheckout="openCheckoutModal" />
                    </div>
                </div>
            </div>
        </div>
        
        <!-- MODAL TELA CHEIA (VER NO NAVEGADOR) -->
        <div v-if="fullPreviewModalOpen" class="modal-browser-overlay" @click.self="closeFullPreviewModal">
            <div class="modal-browser-container shadow-lg">
                <div class="browser-top-bar d-flex align-items-center justify-content-between px-3 py-2 bg-dark text-white">
                    <div class="d-flex align-items-center gap-2">
                        <span class="dot-red"></span>
                        <span class="dot-yellow"></span>
                        <span class="dot-green"></span>
                        <span class="ms-3 small text-muted font-monospace bg-secondary bg-opacity-25 px-3 py-1 rounded-pill">https://dailypag.com.br/{{ form.title ? form.title.toLowerCase().replace(/ /g, '-') : 'pagina' }}</span>
                    </div>
                    <button class="btn btn-sm btn-danger" @click="closeFullPreviewModal" title="Fechar">
                        <i class="bi bi-x-lg"></i> Fechar
                    </button>
                </div>
                <div class="browser-body flex-grow-1 bg-white modern-preview-font overflow-y-auto">
                    <PagePreviewRenderer :form="form" @openCheckout="openCheckoutModal" />
                </div>
            </div>
        </div>

        <!-- MODAL DA IA -->
        <transition name="fade">
            <div v-if="showAiModal" class="ai-modal-overlay" @click.self="closeAiModal">
                <div class="ai-modal-content shadow-lg rounded-4 bg-white p-4">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h5 class="fw-bold mb-0 d-flex align-items-center" style="color: #5F0487;">
                            <i class="bi bi-magic fs-4 me-2 text-warning"></i> Assistente IA
                        </h5>
                        <button class="btn-close" @click="closeAiModal" :disabled="aiLoading"></button>
                    </div>
                    <p class="text-muted small mb-4">Descreva o objetivo do seu negócio e nossa IA criará uma estratégia completa.</p>
                    <div class="mb-4">
                        <textarea v-model="aiObjective" class="form-control form-control-pmg" rows="4" placeholder="Ex: Campanha de arrecadação..." :disabled="aiLoading"></textarea>
                    </div>
                    <div class="d-flex justify-content-end gap-2">
                        <button class="btn btn-pmg-light" @click="closeAiModal" :disabled="aiLoading">Cancelar</button>
                        <button class="btn btn-pmg-primary" @click="generateWithAi" :disabled="aiLoading || aiObjective.length < 5">
                            <span v-if="aiLoading" class="spinner-border spinner-border-sm me-2"></span>
                            Gerar com IA
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- MODAL DE CHECKOUT INTEGRADO -->
        <div v-if="checkoutModalOpen" class="modal-browser-overlay" style="z-index: 2050;" @click.self="closeCheckoutModal">
            <div class="modal-dialog modal-dialog-centered" style="width: 100%; max-width: 480px;">
                <div class="modal-content border-0 shadow-lg rounded-4 overflow-hidden bg-white">
                    <div class="card-body p-4">
                        <button type="button" class="btn-close position-absolute top-0 end-0 m-3" @click="closeCheckoutModal"></button>
                        
                        <div class="text-center mb-3 mt-2">
                            <h6 class="text-muted text-uppercase fw-bold mb-1" style="font-size: 0.75rem;">Apoiar Campanha</h6>
                            <h3 class="fw-bold mb-0" style="color: #5F0487;">{{ activeCheckoutContent.title }}</h3>
                        </div>

                        <div v-if="activeCheckoutContent.allows_fractional && !showCardForm" class="mb-4 p-3 rounded-3" style="background-color: #f8fafc; border: 1px solid #e2e8f0;">
                            <label class="form-label small fw-bold text-dark mb-2">
                                <i class="bi bi-pie-chart-fill text-primary me-1"></i> Valor que deseja apoiar (R$)
                            </label>
                            <div class="input-group input-group-lg shadow-sm">
                                <span class="input-group-text bg-white border-end-0 text-muted fw-bold">R$</span>
                                <input type="text" class="form-control border-start-0 fw-bold text-primary" v-model="displayCheckoutAmount" @input="handleCheckoutMask" placeholder="0,00">
                            </div>
                        </div>

                        <div v-if="!showCardForm">
                            <h6 class="fw-bold mb-2 text-dark" style="font-size: 0.95rem;">Como deseja pagar?</h6>
                            <div class="d-flex flex-row flex-wrap gap-2">
                                <button v-if="activeCheckoutContent.allowed_methods?.pix" class="payment-btn d-flex align-items-center flex-grow-1 text-start p-2 px-3 rounded-3"
                                    :class="{ 'border-primary bg-primary-subtle': selectedMethod === 'pix' }" @click="selectedMethod = 'pix'">
                                    <div class="icon-wrapper bg-white text-primary me-3 rounded-circle d-flex align-items-center justify-content-center shadow-sm" style="width: 38px; height: 38px; border: 1px solid #bfdbfe;">
                                        <i class="bi bi-qr-code fs-5"></i>
                                    </div>
                                    <div><div class="fw-bold text-dark" style="font-size: 0.9rem;">PIX</div><div class="text-muted mt-0" style="font-size: 0.75rem;">Aprovação imediata</div></div>
                                </button>

                                <button v-if="activeCheckoutContent.allowed_methods?.boleto" class="payment-btn d-flex align-items-center flex-grow-1 text-start p-2 px-3 rounded-3"
                                    :class="{ 'border-info bg-info-subtle': selectedMethod === 'boleto' }" @click="selectedMethod = 'boleto'">
                                    <div class="icon-wrapper bg-white text-info me-3 rounded-circle d-flex align-items-center justify-content-center shadow-sm" style="width: 38px; height: 38px; border: 1px solid #a5f3fc;">
                                        <i class="bi bi-upc-scan fs-5"></i>
                                    </div>
                                    <div><div class="fw-bold text-dark" style="font-size: 0.9rem;">Boleto</div><div class="text-muted mt-0" style="font-size: 0.75rem;">Até 2 dias úteis</div></div>
                                </button>

                                <button v-if="activeCheckoutContent.allowed_methods?.credit_card" class="payment-btn d-flex align-items-center flex-grow-1 text-start p-2 px-3 rounded-3"
                                    :class="{ 'border-success bg-success-subtle': selectedMethod === 'credit_card' }" @click="selectedMethod = 'credit_card'">
                                    <div class="icon-wrapper bg-white text-success me-3 rounded-circle d-flex align-items-center justify-content-center shadow-sm" style="width: 38px; height: 38px; border: 1px solid #bbf7d0;">
                                        <i class="bi bi-credit-card fs-5"></i>
                                    </div>
                                    <div><div class="fw-bold text-dark" style="font-size: 0.9rem;">Crédito</div><div class="text-muted mt-0" style="font-size: 0.75rem;">Aprovação na hora</div></div>
                                </button>
                            </div>

                            <div v-if="selectedMethod" class="mt-3 pt-3 border-top border-secondary-subtle">
                                <button class="btn fw-bold w-100 text-white py-3 shadow-sm btn-rmr-primary" @click="selectedMethod === 'credit_card' ? showCardForm = true : simulatePayment()">
                                    {{ selectedMethod === 'credit_card' ? 'Preencher Cartão' : 'Gerar Cobrança Agora' }}
                                </button>
                            </div>
                        </div>

                        <div v-if="showCardForm" class="mt-2">
                            <div class="d-flex align-items-center mb-3 border-bottom pb-2">
                                <button class="btn btn-sm btn-light border me-2" @click="showCardForm = false"><i class="bi bi-arrow-left"></i></button>
                                <h6 class="fw-bold mb-0 text-dark">Pagamento com Cartão</h6>
                            </div>
                            
                            <div class="card-preview-container mb-4">
                                <div class="card-inner" :class="{ 'is-flipped': isFlipped }">
                                    <div class="card-front shadow-sm border">
                                        <div class="d-flex justify-content-between align-items-start mb-3">
                                            <div class="chip"></div>
                                            <i class="bi bi-credit-card fs-2 text-white opacity-75"></i>
                                        </div>
                                        <div class="card-number-display mb-2 fs-5">{{ cardForm.number || '**** **** **** ****' }}</div>
                                        <div class="d-flex justify-content-between">
                                            <div class="text-truncate me-2">
                                                <span class="d-block opacity-50" style="font-size: 8px;">TITULAR</span>
                                                <span class="fw-medium text-uppercase" style="font-size: 13px;">{{ cardForm.name || 'NOME IMPRESSO' }}</span>
                                            </div>
                                            <div class="text-end">
                                                <span class="d-block opacity-50" style="font-size: 8px;">VALIDADE</span>
                                                <span class="fw-medium" style="font-size: 13px;">{{ cardForm.expiry || 'MM/AA' }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card-back shadow-sm border">
                                        <div class="magnetic-strip mt-3"></div>
                                        <div class="px-3 mt-3 text-end">
                                            <span class="d-block text-white opacity-75 mb-1" style="font-size: 9px;">CVV</span>
                                            <div class="text-dark bg-white rounded-1 px-2 py-1 fw-bold fs-6 d-inline-block">{{ cardForm.cvv || '***' }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mb-2">
                                <input type="text" v-model="cardForm.number" class="form-control form-control-sm font-monospace" placeholder="Número do Cartão" maxlength="19">
                            </div>
                            <div class="mb-2">
                                <input type="text" v-model="cardForm.name" class="form-control form-control-sm text-uppercase" placeholder="Nome Impresso">
                            </div>
                            <div class="row g-2 mb-3">
                                <div class="col-6"><input type="text" v-model="cardForm.expiry" class="form-control form-control-sm font-monospace" placeholder="MM/AA"></div>
                                <div class="col-6"><input type="text" v-model="cardForm.cvv" @focus="isFlipped = true" @blur="isFlipped = false" class="form-control form-control-sm font-monospace" placeholder="CVV"></div>
                            </div>
                            <button class="btn fw-bold w-100 text-white py-3 shadow-sm btn-rmr-primary" @click="simulatePayment()">Processar Pagamento</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import ImageCropper from '@/components/ImageCropper.vue';
import PagePreviewRenderer from './PagePreviewRenderer.vue';
import api from '@/services/api';

export default {
    components: {
        ImageCropper,
        PagePreviewRenderer
    },
    data() {
        return {
            loading: false,
            alerts: [],
            fullPreviewModalOpen: false,
            showAiModal: false,
            aiObjective: '',
            aiLoading: false,
            
            checkoutModalOpen: false,
            activeCheckoutContent: {},
            displayCheckoutAmount: '',
            checkoutAmountRaw: 0,
            selectedMethod: null,
            showCardForm: false,
            isFlipped: false,
            cardForm: { number: '', name: '', expiry: '', cvv: '' },

            paymentOptions: [],
            
            form: {
                title: 'Nova Página',
                title_color: '#5F0487',
                template: '',
                sections: []
            },
            
            presets: {
                layout_6: [
                    {
                        type: 'dailypag_crowdfunding',
                        order: 0,
                        content: {
                            title: 'Focinhos com Fome',
                            category: 'CAUSA ANIMAL',
                            location: 'BRASIL',
                            cover_image: '',
                            short_description: 'Todos os dias, centenas de cães e gatos de rua sofrem com a fome e o frio. Nossa missão é distribuir ração e cuidar desses anjos.',
                            pix_key: 'ajude@dailypag.com.br',
                            about_text: '<p class="fw-bold text-dark">Nossa Missão:</p><p>Acreditamos que todo animal merece uma barriga cheia e um lugar quente para dormir.</p>',
                            raised_amount: 1250.00,
                            goal_amount: 5000.00,
                            supporters_count: 84,
                            creator_name: 'Equipe Proteção Animal',
                            payment_gateway_id: null,
                            allowed_methods: { pix: true, boleto: true, credit_card: true },
                            allows_fractional: true,
                            amount: 25.00
                        }
                    }
                ]
            }
        };
    },
    watch: {
        'form.template'(newVal) {
            if (newVal) this.loadPreset(newVal);
            else this.form.sections = []; 
        }
    },
    created() {
        this.form.sections = []; 
        this.fetchPaymentOptions();
    },
    methods: {
        async fetchPaymentOptions() {
            try {
                const response = await api.get('/payment-gateways/options');
                this.paymentOptions = response.data;
            } catch (error) {
                console.error("Erro ao carregar opções de pagamento", error);
            }
        },
        getAvailableGatewaysForSection(section) {
            const methods = section.content.allowed_methods || {};
            return this.paymentOptions.filter(gateway => {
                if (methods.pix && !gateway.allows_pix) return false;
                if (methods.boleto && !gateway.allows_ticket) return false;
                if (methods.credit_card && !gateway.allows_credit_card) return false;
                return true;
            });
        },
        loadPreset(templateKey) {
            if (this.presets[templateKey]) {
                this.form.sections = JSON.parse(JSON.stringify(this.presets[templateKey]));
            }
        },
        addSection(type) {
            let defaultContent = { title: '', bg_color: '#ffffff', accent_color: '#5F0487' };
            if (type === 'dailypag_crowdfunding') defaultContent = JSON.parse(JSON.stringify(this.presets.layout_6[0].content));
            else if (type === 'text') defaultContent.text = '<p>Insira seu texto aqui...</p>';
            else if (type === 'youtube') defaultContent.url = '';
            else if (type === 'contact') { defaultContent.title = 'Fale Conosco'; defaultContent.whatsapp = ''; defaultContent.email = ''; }
            else if (type === 'payment') { defaultContent.button_text = 'Comprar'; defaultContent.payment_gateway_id = null; defaultContent.amount = 50.00; }
            else if (type === 'carousel') { defaultContent.title = 'Nossa Galeria'; defaultContent.images = [{ url: '' }]; }
            this.form.sections.push({ type, content: defaultContent });
        },
        removeSection(index) { this.form.sections.splice(index, 1); },
        moveSection(index, direction) {
            const newIndex = index + direction;
            if (newIndex >= 0 && newIndex < this.form.sections.length) {
                const temp = this.form.sections[index];
                this.form.sections[index] = this.form.sections[newIndex];
                this.form.sections[newIndex] = temp;
            }
        },
        
        handleCroppedBlob(blob, targetObj, property) {
            const reader = new FileReader();
            reader.onload = (e) => { targetObj[property] = e.target.result; };
            reader.readAsDataURL(blob);
        },
        handleImageUpload(event, targetObj, property) {
            const file = event.target.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (e) => { targetObj[property] = e.target.result; };
            reader.readAsDataURL(file);
        },

        handleCurrencyInput(e, obj, key) {
            let val = e.target.value.replace(/\D/g, '');
            let numeric = parseInt(val || '0', 10) / 100;
            obj[key] = numeric;
            e.target.value = numeric.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatCurrency(v) { return parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },

        openCheckoutModal(content) {
            this.activeCheckoutContent = content;
            this.checkoutAmountRaw = content.amount || 0;
            this.displayCheckoutAmount = this.formatCurrency(this.checkoutAmountRaw);
            this.selectedMethod = null;
            this.showCardForm = false;
            this.checkoutModalOpen = true;
        },
        closeCheckoutModal() { this.checkoutModalOpen = false; },
        handleCheckoutMask(e) {
            let val = e.target.value.replace(/\D/g, '');
            this.checkoutAmountRaw = parseInt(val || '0', 10) / 100;
            this.displayCheckoutAmount = this.formatCurrency(this.checkoutAmountRaw);
        },
        simulatePayment() {
            this.showAlert('Sucesso! Simulação de pagamento concluída pelo Gateway selecionado.', 'success');
            this.closeCheckoutModal();
        },

        openFullPreviewModal() { this.fullPreviewModalOpen = true; },
        closeFullPreviewModal() { this.fullPreviewModalOpen = false; },
        openAiModal() { this.showAiModal = true; },
        closeAiModal() { this.showAiModal = false; },
        generateWithAi() {
            this.aiLoading = true;
            setTimeout(() => {
                this.aiLoading = false;
                this.showAlert('Página gerada via IA (Simulação)!');
                this.closeAiModal();
            }, 2000);
        },
        
        savePage() {
            this.loading = true;
            setTimeout(() => { this.loading = false; this.showAlert('Página salva com sucesso!'); }, 1000);
        },
        showAlert(message, type = 'success') {
            const id = Date.now();
            this.alerts.push({ id, message, type, icon: 'bi-check-circle-fill' });
            setTimeout(() => { this.alerts = this.alerts.filter(a => a.id !== id); }, 4000);
        },
        formatText(command) { document.execCommand(command, false, null); }
    }
}
</script>

<style scoped>
.main-content { background-color: #f8fafc; min-height: 100vh; padding: 2rem; }
.card { border-radius: 16px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02); }
.btn-pmg-primary { background: #5F0487; color: #fff; border: none; border-radius: 10px; padding: 10px 20px; font-weight: 500; }
.btn-pmg-light { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; color: #64748b; padding: 10px 20px; }
.form-control-pmg { border-radius: 10px; border: 1px solid #e2e8f0; padding: 12px; }

.modal-browser-overlay, .ai-modal-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background-color: rgba(15, 23, 42, 0.75); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 1rem; backdrop-filter: blur(5px); }
.modal-browser-container { width: 100%; max-width: 1200px; height: 90vh; background: #ffffff; border-radius: 16px; display: flex; flex-direction: column; overflow: hidden; }
.ai-modal-content { width: 100%; max-width: 500px; }

.payment-btn { background-color: #ffffff; border: 2px solid #e2e8f0; transition: all 0.2s ease-in-out; cursor: pointer; }
.payment-btn:hover { border-color: #5F0487; background-color: #f8fafc; transform: translateY(-2px); }
.payment-btn.border-primary { border-color: #0d6efd !important; }
.payment-btn.border-info { border-color: #0dcaf0 !important; }
.payment-btn.border-success { border-color: #198754 !important; }

.card-preview-container { perspective: 1000px; width: 100%; max-width: 320px; height: 195px; margin: 0 auto; }
.card-inner { position: relative; width: 100%; height: 100%; transition: transform 0.8s; transform-style: preserve-3d; }
.card-inner.is-flipped { transform: rotateY(180deg); }
.card-front, .card-back { position: absolute; width: 100%; height: 100%; backface-visibility: hidden; border-radius: 16px; padding: 20px; color: white; }
.card-front { background: linear-gradient(135deg, #5F0487 0%, #101930 100%); display: flex; flex-direction: column; justify-content: space-between; }
.card-back { background: linear-gradient(135deg, #1a202c 0%, #000000 100%); transform: rotateY(180deg); padding: 0; }
.chip { width: 42px; height: 32px; background: linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%); border-radius: 6px; }
.magnetic-strip { width: 100%; height: 40px; background: #000; }
.card-number-display { font-family: 'Courier New', monospace; letter-spacing: 2px; }

.btn-rmr-primary { background-color: #5F0487; color: white; }
.btn-rmr-primary:hover { background-color: #4a036a; }
</style>